// Pantalla bloqueante que se muestra cuando un usuario tiene la cuenta creada
// pero aún NO ha verificado su email. Aparece tras login si:
//   - user.emailVerified === false
//   - userData.requiresEmailVerification === true
//
// Permite reenviar el email de verificación o cerrar sesión.

import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { sendEmailVerification } from 'firebase/auth';
import { auth, functions } from '@config/firebase';
import { useAuth, useToast } from '@contexts';
import { AuthLayout } from './AuthLayout';

export const EmailVerificationGate = ({ user }) => {
  const { logout } = useAuth();
  const { addToast } = useToast();
  const [resending, setResending] = useState(false);
  const [checking, setChecking] = useState(false);

  const handleResend = async () => {
    setResending(true);
    try {
      // Intentar primero con la Cloud Function (envía con plantilla custom)
      try {
        const fn = httpsCallable(functions, 'resendVerificationEmail');
        await fn({ email: user.email });
        addToast('Email reenviado. Revisa tu bandeja.', 'success');
      } catch {
        // Fallback: usar Firebase Auth directo (plantilla genérica)
        await sendEmailVerification(user);
        addToast('Email reenviado. Revisa tu bandeja.', 'success');
      }
    } catch (err) {
      addToast('No se pudo reenviar el email', 'error');
    }
    setResending(false);
  };

  const handleCheck = async () => {
    setChecking(true);
    try {
      await user.reload();
      if (user.emailVerified) {
        addToast('¡Email verificado! Cargando tu panel…', 'success');
        // Recargar la página para que AuthContext detecte el cambio
        setTimeout(() => window.location.reload(), 800);
      } else {
        addToast('Aún no aparece como verificado. Pulsa el enlace del email primero.', 'info');
      }
    } catch {
      addToast('Error al comprobar', 'error');
    }
    setChecking(false);
  };

  return (
    <AuthLayout maxWidth={520} slideKey="verify">
        <div className="login-card">
          <div className="login-header">
            <a href="/" style={{display: 'block', textDecoration: 'none'}}>
              <div className="login-logo">
                <img src="/assets/icons/icon-512x512.png" alt="TorneosPro" />
              </div>
            </a>
            <h1 className="login-title" style={{fontSize: '1.4rem'}}>
              <i className="fa-solid fa-envelope" style={{marginRight: 8}}></i>
              Verifica tu email
            </h1>
            <p className="login-subtitle">Tu cuenta está creada pero pendiente</p>
          </div>
          <div className="login-body" style={{textAlign: 'center'}}>
            <i className="fa-solid fa-envelope-circle-check" style={{
              fontSize: '3.5rem', color: 'var(--color-yellow)', marginBottom: 16,
            }}></i>

            <p style={{fontSize: '1rem', marginBottom: 8, lineHeight: 1.6}}>
              Te hemos enviado un email a:
            </p>
            <p style={{
              fontSize: '1.05rem', fontWeight: 700, color: 'var(--color-yellow)',
              background: 'rgba(240,216,120,0.08)', padding: '10px 16px',
              borderRadius: 'var(--radius-md)', margin: '0 0 24px',
              wordBreak: 'break-all',
            }}>
              {user.email}
            </p>

            <p style={{fontSize: '0.95rem', opacity: 0.85, marginBottom: 24, lineHeight: 1.6}}>
              Pulsa el enlace del email para activar tu cuenta. Después vuelve aquí y haz click en <strong>"Ya verifiqué"</strong>.
            </p>

            <div style={{
              padding: '12px 16px',
              background: 'rgba(255, 165, 0, 0.08)',
              border: '1px solid rgba(255, 165, 0, 0.3)',
              borderRadius: 'var(--radius-md)',
              fontSize: '0.85rem',
              textAlign: 'left',
              marginBottom: 24,
              lineHeight: 1.5,
            }}>
              <strong style={{color: '#ffa500'}}>
                <i className="fa-solid fa-triangle-exclamation" style={{marginRight: 6}}></i>
                ¿No te llega?
              </strong>
              <ul style={{margin: '8px 0 0', paddingLeft: 20, opacity: 0.9}}>
                <li>Revisa la carpeta de SPAM o promociones</li>
                <li>Espera 1-2 minutos antes de reenviar</li>
                <li>Comprueba que el email sea correcto</li>
              </ul>
            </div>

            <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
              <button
                onClick={handleCheck}
                disabled={checking}
                className="btn btn-block"
                style={{
                  background: 'var(--color-yellow)', color: '#0a0a0a',
                  fontWeight: 700, border: 'none',
                }}
              >
                {checking ? <div className="spinner" style={{width: 18, height: 18, borderWidth: 2}}></div> : (
                  <><i className="fa-solid fa-rotate-right"></i> Ya verifiqué, comprobar</>
                )}
              </button>

              <button
                onClick={handleResend}
                disabled={resending}
                className="btn btn-block"
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  color: 'var(--text-primary)',
                  border: '1px solid rgba(255,255,255,0.15)',
                }}
              >
                {resending ? <div className="spinner" style={{width: 18, height: 18, borderWidth: 2}}></div> : (
                  <><i className="fa-solid fa-paper-plane"></i> Reenviar email</>
                )}
              </button>

              <button
                onClick={logout}
                className="btn btn-ghost btn-block"
                style={{fontSize: '0.85rem', opacity: 0.7}}
              >
                <i className="fa-solid fa-sign-out-alt"></i> Cerrar sesión
              </button>
            </div>
          </div>
        </div>
    </AuthLayout>
  );
};

export default EmailVerificationGate;
